import * as React from "react";
import { observer, inject } from "mobx-react";
import { UncontrolledTooltip } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import { CustomerLink } from "../common/CustomerLink";
import CustomerSelect from "../common/CustomerSelect";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";

import { CustomerEndpoints } from "../../endpoints/CustomerEndpoints";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faBan, faEdit, faTrashAlt, faMapPin, faMapMarkedAlt, faPercentage } from "@fortawesome/free-solid-svg-icons";

import { EditableSubCard } from "../common/EditableCard";

import { Contact, Business, ExtractFriendlyName, SimpleCustomer, Customer, BusinessContactRelationship } from "../../model/Customer";
import { HorseForSaleOwnership } from "src/model/HorseForSale";

export interface PropsStore {
    customerEndpoints?: CustomerEndpoints;
}

export interface PropsComponent {
    isEditable: boolean;
    // handleDialogCustome: () => function;
    onChange: (owners: Array<HorseForSaleOwnership>) => void;
    owners: Array<HorseForSaleOwnership> | null;
}

type Props = PropsComponent & PropsStore;

export interface State {
    selectedOwnerContact: SimpleCustomer | null;
    selectedOwnerPecentage: string;
    wasValidated: boolean;
    isWarning: boolean;
    isInvalid: boolean;
    percentageMessage: string;
}

@inject("customerEndpoints")
@observer
export default class OwnersApplication extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selectedOwnerContact: null,
            selectedOwnerPecentage: "",
            isWarning: false,
            wasValidated: false,
            isInvalid: false,
            percentageMessage: ""
        };
    }

    delete = (relationship: HorseForSaleOwnership | null) => async (event: React.MouseEvent<any>) => {
        if (!relationship) {
            return;
        }
        const goAhead = await SimpleConfirmation(
            `Are you sure you'd like to remove ${ExtractFriendlyName(
                relationship!.owner
            )} as an owner? Invoices for the ownership of this horse may have already been generated for this owner. 
            If invoices have already been generated, please remember to update the invoices for the "Entry Fee" 
            (in the Selection tab) and for any "Missing Documentation" (in the Documents & Compliance tab).`
        );
        if (!goAhead) {
            return;
        }

        const copy = this.copyOwnersFromProps();
        const i = copy.findIndex((r: HorseForSaleOwnership) => r.owner.id === relationship.owner.id);
        if (i >= 0) {
            copy.splice(i, 1);
        }
        this.props.onChange(copy);
    };

    addOwner = (owner: SimpleCustomer, percentage: number) => {
        if (this.props.owners) {
            this.props.onChange([this.buildOwnership(owner, percentage), ...this.props.owners]);
            this.setState({ selectedOwnerContact: null, selectedOwnerPecentage: "" });
        }
    };

    submitOwners = (e: React.FormEvent): void => {
        e.preventDefault();

        if (this.formValidation() && this.state.selectedOwnerContact && this.state.selectedOwnerPecentage) {
            this.addOwner(this.state.selectedOwnerContact, parseFloat(this.state.selectedOwnerPecentage));
        }
    };

    formValidation = (): boolean => {
        const percentage = parseFloat(this.state.selectedOwnerPecentage);
        const totalPercentage = this.calculatePercentage();

        if (this.state.selectedOwnerContact === null) {
            this.setState({ isInvalid: true, wasValidated: true });
            return false;
        } else if (this.state.selectedOwnerPecentage === "") {
            this.setState({ percentageMessage: "Please add a percentage to Owner.", wasValidated: true });
            return false;
        } else if (percentage < 1 || percentage > 100) {
            this.setState({ percentageMessage: "Please provide a percentage between 1 and 100.", wasValidated: true });
            return false;
        } else if (totalPercentage + parseFloat(this.state.selectedOwnerPecentage) > 100) {
            this.setState({ isWarning: true });
            return false;
        } else {
            this.setState({ isInvalid: false, wasValidated: false, isWarning: false });
            return true;
        }
    };

    public render() {
        const percentage = this.calculatePercentage();
        const percentageAt100 = percentage === 100;

        const columns = [
            {
                dataField: "owner",
                text: "Owner Name",
                formatter: (cellContent: SimpleCustomer, row: SimpleCustomer) => {
                    return <CustomerLink customer={cellContent} />;
                }
            },
            {
                dataField: "percentage",
                text: "Percentage"
            },
            {
                dataField: "owner.id",
                text: this.props.isEditable ? "Actions" : "",
                headerStyle: { width: "100px" },
                formatter: (cellContent: any, row: HorseForSaleOwnership) => {
                    return (
                        <>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm read-only-hidden"
                                id={`tooltipDelete${row.owner.id}`}
                                title="Delete Owner"
                                onClick={this.delete(row)}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                            <UncontrolledTooltip placement="top" target={`tooltipDelete${row.owner.id}`} delay={0} className="tooltipstrap">
                                Delete Owner
                            </UncontrolledTooltip>
                        </>
                    );
                }
            }
        ];

        return (
            <EditableSubCard label="Registered Owner(s)">
                <>
                    <form
                        id="owners-form"
                        className={"needs-validation " + (this.state.wasValidated ? " was-validated" : "")}
                        onSubmit={e => this.submitOwners(e)}
                        noValidate={true}
                    >
                        {this.state.isWarning && (
                            <div className="form-row">
                                <div className="col-12">
                                    <div className="alert alert-warning text-center" role="alert">
                                        The total ownership percentage cannot be more than 100%.
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="form-row">
                            <div className="form-group col-sm-12 col-md-12">
                                <BootstrapTable
                                    keyField="owner.id"
                                    hover={true}
                                    bordered={false}
                                    bootstrap4={true}
                                    striped={true}
                                    columns={columns}
                                    //data={this.props.isEditable ? this.state.owners : horseForSale.owners}
                                    data={this.props.owners || []}
                                    noDataIndication={() => "No Owners linked to Horse at the moment"}
                                />
                            </div>
                        </div>
                        {this.props.isEditable && (
                            <div className="form-row">
                                <div className="form-group col-sm-12 col-md-5">
                                    <label htmlFor="selectedOwnerContact">Owner: </label>
                                    <CustomerSelect
                                        isInvalid={this.state.isInvalid}
                                        isDisabledLink={!this.props.isEditable || percentageAt100}
                                        customerEndpoints={this.props.customerEndpoints!}
                                        include="BOTH"
                                        showCreate={true}
                                        value={this.state.selectedOwnerContact}
                                        noRedirect={true}
                                        onCreate={selectedOwnerContact => this.setState({ selectedOwnerContact })}
                                        onChange={selectedOwnerContact => this.setState({ selectedOwnerContact })}
                                        excludeCustomers={this.props.owners ? this.props.owners.map(o => o.owner.id) : []}
                                    />
                                    {this.state.isInvalid && <div className="invalid-feedback">Please select a owner(s).</div>}
                                </div>
                                <div className="form-group col-sm-12 col-md-4">
                                    <label htmlFor="selectedOwnerPecentage">Percentage: </label>
                                    <input
                                        type="number"
                                        name="selectedOwnerPecentage"
                                        className="form-control"
                                        placeholder="%"
                                        min={1}
                                        max={100}
                                        maxLength={5}
                                        step={0.1}
                                        disabled={!this.props.isEditable || percentageAt100}
                                        value={this.state.selectedOwnerPecentage}
                                        onChange={e => {
                                            e.preventDefault();
                                            this.setState({ selectedOwnerPecentage: e.target.value });
                                        }}
                                        required={true}
                                    />
                                    <div className="invalid-feedback">{this.state.percentageMessage}</div>
                                </div>
                                <div className="form-group col-sm-12 col-md-3 pt-4">
                                    <button
                                        type="submit"
                                        form="owners-form"
                                        onClick={this.submitOwners}
                                        disabled={!this.props.isEditable || percentageAt100}
                                        className="btn btn-info mt-2 btn-block"
                                    >
                                        {" "}
                                        Add Ownership
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>
                </>
            </EditableSubCard>
        );
    }

    private calculatePercentage(): number {
        return (this.props.owners || []).map(o => o.percentage).reduce((a, b) => a + b, 0);
    }

    private copyOwnersFromProps(owners: Array<HorseForSaleOwnership> = this.props.owners!): Array<HorseForSaleOwnership> {
        return JSON.parse(JSON.stringify(owners));
    }

    private buildOwnership(owner: SimpleCustomer, percentage: number): HorseForSaleOwnership {
        return {
            owner,
            percentage,
            entryFeeInvoice: null,
            cogginsInvoice: null,
            registrationInvoice: null,
            commissionInvoice: null
        };
    }
}



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/OwnersApplication.tsx